import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import SchemaForm from "@/components/SchemaForm";
import { eur, dateIT } from "@/lib/format";
import {
  ArrowLeft, Save, Sparkles, Globe, Image as ImageIcon, Star, Trash2, Plus,
  UploadCloud, FileText, Receipt, CalendarPlus, X, Loader2, Eye,
} from "lucide-react";

const tabs = [
  { key: "details", label: "Dettagli" },
  { key: "photos", label: "Foto" },
  { key: "rules", label: "Regole" },
  { key: "rates", label: "Tariffe" },
  { key: "bookings", label: "Prenotazioni" },
];

export default function PropertyEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [prop, setProp] = useState(null);
  const [tab, setTab] = useState("details");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const load = async () => {
    const [s, p] = await Promise.all([api.get("/schema/property"), api.get(`/properties/${id}`)]);
    setSchema(s.data); setProp(p.data);
  };
  useEffect(() => { load(); }, [id]);

  if (!prop || !schema) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/properties/${id}`, {
        data: prop.data, enabled_on_hw: prop.enabled_on_hw,
        standard_rules_enabled: prop.standard_rules_enabled || [], custom_rules: prop.custom_rules || [],
      });
      setProp({ ...data, photos: prop.photos });
      toast.success("Scheda salvata e sincronizzata con Home Smart");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/properties/${id}/ai-description`, { property_data: prop.data });
      setProp({ ...prop, data: { ...prop.data, description_html: data.description_html } });
      toast.success(`Descrizione generata${data.cost_cents ? ` (−${eur(data.cost_cents)})` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Servizio AI non disponibile, riprova tra poco");
    } finally { setAiLoading(false); }
  };

  const aiSlots = {
    description_html: (
      <button type="button" onClick={generateAI} disabled={aiLoading} data-testid="property-ai-generate-button"
        className="bg-[#E3FF00] text-neutral-900 font-semibold px-3.5 py-2 rounded-lg shadow-sm hover:brightness-95 transition-all flex items-center gap-2 text-sm disabled:opacity-60">
        {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {aiLoading ? "Generazione..." : "Genera con AI"}
      </button>
    ),
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/properties")} className="h-9 w-9 rounded-lg border border-border grid place-items-center hover:bg-muted shrink-0"><ArrowLeft className="h-4 w-4" /></button>
          <div className="min-w-0">
            <h1 className="font-display text-xl sm:text-2xl font-bold truncate">{prop.data?.name || "Nuovo Immobile"}</h1>
            <p className="text-xs text-muted-foreground">ID HW: {prop.external_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/properties/${id}/preview`)} data-testid="property-view-as-client-button" className="bg-primary text-primary-foreground font-medium px-3.5 py-2.5 rounded-lg shadow-sm hover:brightness-95 active:scale-[0.98] transition-all flex items-center gap-2">
            <Eye className="h-4 w-4" /> <span className="hidden sm:inline">Vedi come cliente</span>
          </button>
          <label className="flex items-center gap-2 text-sm cursor-pointer bg-card border border-border rounded-lg px-3 py-2" data-testid="property-holidayworld-toggle">
            <Globe className="h-4 w-4 text-primary" />
            <span className="hidden sm:inline">Pubblica su HolidayWorld</span>
            <input type="checkbox" checked={!!prop.enabled_on_hw} onChange={(e) => setProp({ ...prop, enabled_on_hw: e.target.checked })} className="accent-[hsl(var(--primary))] h-4 w-4" />
          </label>
          <button onClick={save} disabled={saving} className="bg-primary text-primary-foreground font-medium px-4 py-2.5 rounded-lg shadow-sm hover:opacity-95 active:scale-[0.98] transition-all flex items-center gap-2 disabled:opacity-50" data-testid="property-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-border overflow-x-auto no-scrollbar">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-card rounded-xl border border-border/80 p-5 sm:p-7">
        {tab === "details" && <SchemaForm schema={schema} data={prop.data || {}} onChange={(d) => setProp({ ...prop, data: d })} aiSlots={aiSlots} />}
        {tab === "photos" && <PhotosTab prop={prop} reload={load} />}
        {tab === "rules" && <RulesTab schema={schema} prop={prop} setProp={setProp} />}
        {tab === "rates" && <RatesTab propId={id} />}
        {tab === "bookings" && <BookingsTab prop={prop} />}
      </div>
    </div>
  );
}

function PhotosTab({ prop, reload }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    try { await api.post(`/properties/${prop.id}/photos`, fd); await reload(); toast.success("Foto caricate"); }
    catch { toast.error("Errore caricamento"); }
    finally { setUploading(false); }
  };
  const setCover = async (pid) => { await api.put(`/properties/${prop.id}/photos/${pid}/cover`); await reload(); };
  const del = async (pid) => { await api.delete(`/properties/${prop.id}/photos/${pid}`); await reload(); };

  return (
    <div>
      <div onClick={() => inputRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
        className="border-2 border-dashed border-border hover:border-primary/60 rounded-xl p-8 text-center transition-all bg-muted/30 cursor-pointer" data-testid="property-photo-upload-zone">
        {uploading ? <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary" /> : <UploadCloud className="h-8 w-8 mx-auto text-muted-foreground" />}
        <p className="text-sm mt-3 font-medium">Trascina le foto qui o clicca per caricare</p>
        <p className="text-xs text-muted-foreground mt-1">Le foto sono salvate in modo permanente (object storage)</p>
        <input ref={inputRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => upload(e.target.files)} />
      </div>
      {prop.photos?.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-5">
          {prop.photos.map((ph) => (
            <div key={ph.id} className="relative group aspect-square rounded-lg overflow-hidden border border-border">
              <img src={fileUrl(ph.url)} alt="" className="h-full w-full object-cover" />
              {ph.is_cover && <span className="absolute top-2 left-2 bg-emerald-700/90 text-white text-[11px] px-2 py-0.5 rounded-md font-semibold">Copertina</span>}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {!ph.is_cover && <button onClick={() => setCover(ph.id)} className="h-8 w-8 rounded-full bg-white text-emerald-700 grid place-items-center" data-testid="property-photo-cover-toggle" title="Imposta copertina"><Star className="h-4 w-4" /></button>}
                <button onClick={() => del(ph.id)} className="h-8 w-8 rounded-full bg-white text-destructive grid place-items-center"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RulesTab({ schema, prop, setProp }) {
  const std = schema.standard_rules || [];
  const enabled = prop.standard_rules_enabled || [];
  const custom = prop.custom_rules || [];
  const toggleStd = (label) => setProp({ ...prop, standard_rules_enabled: enabled.includes(label) ? enabled.filter((x) => x !== label) : [...enabled, label] });
  const [nr, setNr] = useState({ label: "", type: "divieto" });
  const addCustom = () => { if (!nr.label.trim()) return; setProp({ ...prop, custom_rules: [...custom, { ...nr }] }); setNr({ label: "", type: "divieto" }); };
  const delCustom = (i) => setProp({ ...prop, custom_rules: custom.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-7">
      <div>
        <h3 className="font-display text-lg font-semibold mb-3">Regole standard</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          {std.map((r) => (
            <label key={r.key} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/40 cursor-pointer transition-colors">
              <input type="checkbox" checked={enabled.includes(r.label)} onChange={() => toggleStd(r.label)} className="accent-[hsl(var(--primary))] h-4 w-4" />
              <span className="text-sm">{r.label}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="pt-6 border-t border-border/60">
        <h3 className="font-display text-lg font-semibold mb-1">Regole personalizzate</h3>
        <p className="text-xs text-muted-foreground mb-3">Aggiungi regole illimitate specifiche per questo immobile.</p>
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <input value={nr.label} onChange={(e) => setNr({ ...nr, label: e.target.value })} placeholder="Testo della regola..." className="flex-1 bg-background border border-input rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" data-testid="custom-rule-input" />
          <select value={nr.type} onChange={(e) => setNr({ ...nr, type: e.target.value })} className="bg-background border border-input rounded-lg px-3 py-2.5 text-sm">
            <option value="divieto">Divieto</option><option value="permesso">Permesso</option><option value="orario">Orario</option><option value="info">Info</option>
          </select>
          <button onClick={addCustom} className="bg-primary text-primary-foreground px-4 py-2.5 rounded-lg flex items-center gap-2 text-sm font-medium" data-testid="custom-rule-add"><Plus className="h-4 w-4" /> Aggiungi</button>
        </div>
        <div className="space-y-2">
          {custom.map((r, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border border-border">
              <span className="text-sm"><span className="text-[11px] uppercase font-semibold text-primary mr-2">{r.type}</span>{r.label}</span>
              <button onClick={() => delCustom(i)} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
            </div>
          ))}
          {custom.length === 0 && <p className="text-sm text-muted-foreground py-2">Nessuna regola personalizzata.</p>}
        </div>
      </div>
    </div>
  );
}

function RatesTab({ propId }) {
  const [rates, setRates] = useState([]);
  const [f, setF] = useState({ label: "", start: "", end: "", price: "", min_nights: 1 });
  const load = () => api.get(`/properties/${propId}/rates`).then(({ data }) => setRates(data));
  useEffect(() => { load(); }, [propId]);
  const add = async () => {
    if (!f.label || !f.price) return toast.error("Compila nome e prezzo");
    await api.post(`/properties/${propId}/rates`, { label: f.label, start: f.start, end: f.end, price_cents: Math.round(Number(f.price) * 100), min_nights: Number(f.min_nights) });
    setF({ label: "", start: "", end: "", price: "", min_nights: 1 }); load(); toast.success("Tariffa aggiunta");
  };
  const del = async (id) => { await api.delete(`/properties/${propId}/rates/${id}`); load(); };
  const inp = "bg-background border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <input className={inp + " col-span-2"} placeholder="Periodo (es. Alta stagione)" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
        <input type="date" className={inp} value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} />
        <input type="date" className={inp} value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} />
        <input type="number" className={inp} placeholder="€/notte" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
        <button onClick={add} className="bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm font-medium flex items-center justify-center gap-1"><Receipt className="h-4 w-4" /> Aggiungi</button>
      </div>
      <div className="divide-y divide-border/60">
        {rates.map((r) => (
          <div key={r.id} className="py-3 flex items-center justify-between">
            <div><div className="font-medium text-sm">{r.label}</div><div className="text-xs text-muted-foreground">{dateIT(r.start)} → {dateIT(r.end)} · min {r.min_nights} notti</div></div>
            <div className="flex items-center gap-4"><span className="font-mono-data font-semibold">{eur(r.price_cents)}</span><button onClick={() => del(r.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button></div>
          </div>
        ))}
        {rates.length === 0 && <p className="text-sm text-muted-foreground py-3">Nessuna tariffa impostata.</p>}
      </div>
    </div>
  );
}

function BookingsTab({ prop }) {
  const [bookings, setBookings] = useState([]);
  const [f, setF] = useState({ guest_name: "", guest_email: "", guest_phone: "", checkin: "", checkout: "", guests: 2 });
  const load = () => api.get(`/properties/${prop.id}/bookings`).then(({ data }) => setBookings(data));
  useEffect(() => { load(); }, [prop.id]);
  const add = async () => {
    if (!f.guest_name || !f.checkin || !f.checkout) return toast.error("Compila nome e date");
    await api.post(`/properties/${prop.id}/bookings`, { ...f, source: "manual" });
    setF({ guest_name: "", guest_email: "", guest_phone: "", checkin: "", checkout: "", guests: 2 }); load(); toast.success("Prenotazione aggiunta");
  };
  const del = async (id) => { await api.delete(`/bookings/${id}`); load(); };
  const downloadSheet = async (b) => {
    try {
      const res = await api.get(`/bookings/${b.id}/travel-sheet`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `foglio-viaggio-${b.guest_name}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Errore generazione PDF"); }
  };
  const inp = "bg-background border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">Inserisci prenotazioni esterne manuali per tenere aggiornato il calendario.</p>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <input className={inp + " col-span-2"} placeholder="Nome ospite" value={f.guest_name} onChange={(e) => setF({ ...f, guest_name: e.target.value })} data-testid="booking-guest-input" />
        <input type="date" className={inp} value={f.checkin} onChange={(e) => setF({ ...f, checkin: e.target.value })} />
        <input type="date" className={inp} value={f.checkout} onChange={(e) => setF({ ...f, checkout: e.target.value })} />
        <input type="number" className={inp} placeholder="Ospiti" value={f.guests} onChange={(e) => setF({ ...f, guests: Number(e.target.value) })} />
        <button onClick={add} className="bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm font-medium flex items-center justify-center gap-1" data-testid="calendar-manual-booking-button"><CalendarPlus className="h-4 w-4" /> Aggiungi</button>
      </div>
      <div className="divide-y divide-border/60">
        {bookings.map((b) => (
          <div key={b.id} className="py-3 flex flex-wrap items-center justify-between gap-2">
            <div><div className="font-medium text-sm">{b.guest_name}</div><div className="text-xs text-muted-foreground">{dateIT(b.checkin)} → {dateIT(b.checkout)} · {b.guests} ospiti</div></div>
            <div className="flex items-center gap-3">
              <button onClick={() => downloadSheet(b)} className="text-xs flex items-center gap-1 text-primary hover:underline" data-testid="travel-sheet-pdf-download"><FileText className="h-4 w-4" /> Foglio di Viaggio</button>
              <button onClick={() => del(b.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {bookings.length === 0 && <p className="text-sm text-muted-foreground py-3">Nessuna prenotazione.</p>}
      </div>
    </div>
  );
}
